/**
 * Two-stage swing resolution (HANDOFF §3.4) — Agent B (shot pipeline).
 *
 * Converts a locked backswing (POWER) + impact (ACCURACY) into shot parameters
 * (power, aim angle, lateral push-off). PURE: takes input + rng, returns shot
 * params. NO DOM, NO direct randomness (rng injected for the club's base wobble).
 *
 * PHASE 1 CONTRACT — Agent A calls `resolveSwing` from the reducer; Agent B
 * implements it using SWING/DEFENSE constants from ./constants.ts. This module
 * is a prime suspect for the §5 bug (pushOff applied when impact is within band):
 * a clean impact MUST yield exactly `pushOff === 0`. Do NOT hardcode numbers here.
 */
import type { ShotParams, SwingInput, Rng } from './types.ts';
import { SWING, DEFENSE, DEG_TO_RAD, EPSILON, CARD_MAP } from './constants.ts';

export type { ShotParams, SwingInput } from './types.ts';

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Map a locked backswing meter to launch power (§3.4).
 *
 * `power = backswingMeter * (powerReference / backSweet)` so a sweet backswing
 * (meter === backSweet) maps to exactly `powerReference` (the calibrated "reach
 * the pin" power, given club carry ≈ 1.0). An off-sweet backswing costs a small
 * amount of distance either way, then the result is clamped to [powerMin, powerMax].
 * Rough applies its power multiplier on top.
 */
function resolvePower(backswingMeter: number, rough: boolean): number {
  // The locked backswing fraction IS the power: 0..1 of the box's SET range (§3.3,
  // Golf-Clash dial-it-in). Rough saps distance. The fraction → distance (and the
  // per-box distance variance) is resolved at ball spawn, where the hole fraction is
  // known, so a sweet swing no longer auto-reaches the pin.
  const bv = rough ? backswingMeter * DEFENSE.roughPowerMultiplier : backswingMeter;
  return clamp(bv, 0, 1);
}

/**
 * The signed impact miss BEYOND the dead-on band (§3.4). Exactly 0 when the locked impact is
 * within `band` of `impSweet` (a clean strike) — the §5 invariant: a clean impact contributes
 * zero lateral push (so perfect timing aces). `band` is ACCURACY-scaled (wider = more forgiving).
 */
function impactMissBeyondBand(impactMeter: number, band: number): number {
  const err = impactMeter - SWING.impSweet; // signed: + late (push), − early (pull)
  // EPSILON guard: at the exact band edge the subtraction below lands ~1e-14 off
  // zero in floating point; treat that as a clean strike (pushOff must be 0). §5.
  if (Math.abs(err) <= band + EPSILON) return 0;
  return err > 0 ? err - band : err + band;
}

/**
 * Lateral push magnitude (px) for a signed impact miss BEYOND the band (§3.4), via a
 * TWO-STAGE ramp: a gentle inner slope up to `pushOffKnee` so a SLIGHT mis-time stays
 * near the green (skilled play / §5b), then a STEEP outer slope so a CLEAR mis-time
 * flies past the green container. Sign follows `impMiss`; clamped to `pushOffMax`.
 */
function lateralPush(impMiss: number): number {
  const a = Math.abs(impMiss);
  const inner = Math.min(a, SWING.pushOffKnee) * SWING.pushOffGain;
  const outer = Math.max(0, a - SWING.pushOffKnee) * SWING.pushOffSteepGain;
  return Math.sign(impMiss) * Math.min(SWING.pushOffMax, inner + outer);
}

/**
 * Resolve a two-stage swing into shot parameters (§3.4).
 *
 * A CLEAN strike flies DEAD STRAIGHT — there is no random wobble, so perfect distance +
 * direction timing aces every club (modulo wind + slope). Angle = the aim; only ROUGH (a
 * defense debuff) sprays it. Lateral error comes ONLY from MIS-timing the impact meter: the
 * card's ACCURACY sets the clean sweet-spot width and its FORGIVENESS sets the miss penalty.
 */
export function resolveSwing(input: SwingInput, rng: Rng): ShotParams {
  const power = resolvePower(input.backswingMeter, input.rough); // backswing FRACTION (0..1)

  // Aim straight; only ROUGH adds a random spray (the one rng draw, and only when roughed).
  const angle = input.rough
    ? input.aimAngle + (rng() - 0.5) * 2 * DEFENSE.roughSpreadDegrees * DEG_TO_RAD
    : input.aimAngle;

  // Lateral push from a MIS-timed impact only. Within the ACCURACY-widened band impMiss === 0,
  // so pushOff === 0 exactly (a clean strike is dead straight — §5). FORGIVENESS scales the
  // punishment beyond the band. Direction (createBall perp = angle + 90° = the shooter's RIGHT):
  // a late impact (impMiss > 0) pulls the ball LEFT, an early one pushes it RIGHT (leading minus).
  const band = SWING.impBand * CARD_MAP.impactBand(input.club.accuracy);
  const impMiss = impactMissBeyondBand(input.impactMeter, band);
  const pushOff = impMiss === 0 ? 0 : -lateralPush(impMiss) * CARD_MAP.pushScale(input.club.forgiveness);

  return { power, angle, pushOff };
}
